import {AttachFile, Close, KeyboardDoubleArrowRight, SendOutlined, StopCircleOutlined} from "@mui/icons-material";
import {Box, Button, IconButton, InputAdornment, Modal, TextField, Typography} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import {validateFileForIntent, getCurrentTime} from "../utils/assistantUtils";
import FileDrop from "./FileDrop";
import IpAssistantTask from "./IpAssistantTask";
import Messages from "./Messages";
import PropTypes from "prop-types";

const Chats = ({
                 switchedComponent,
                 uploadedFiles,
                 chatStarted,
                 messages,
                 loading,
                 handleModuleSubmit,
                 disabledFields,
                 scrollToLastItem,
                 setSwitchedComponent,
                 chatInput,
                 setChatInput,
                 charactersLimit,
                 handleChatSubmit,
                 setAssistantSidebarOpen,
                 intentList,
                 autoFocusRef,
                 chatSessionId,
                 setMessages,
                 setChatStarted,
                 setLoading,
                 handleStop,
                 setUploadedFiles,
                 intentName,
                 clickedSubmit,
                 setFilterData,
                 filterData,
                 dialogActionType,
                 slotName,
               }) => {
  const disabledButtonCondition =
      loading || !chatInput.trim();
  const [cancelButton, setCancelButton] = useState(false);
  const [fileNamesSet, setFileNamesSet] = useState(new Set());
  const fileDropRef = useRef(null);

  const handleCancelOpen = () => {
    setCancelButton(true);
  };

  const handleCancelClose = () => {
    setCancelButton(false);
  };

  const handleDelete = (index) => {
    if (loading) return;
    const updatedFiles = uploadedFiles.filter((_, i) => i !== index);
    const updatedFileNamesSet = new Set(
        updatedFiles.map((file) => `${file.name}.${file.type}`)
    );
    setFileNamesSet(updatedFileNamesSet);
    setUploadedFiles(updatedFiles);
  };

  useEffect(() => {
    if (uploadedFiles.length > 0 && fileDropRef.current) {
      fileDropRef.current.focus();
    }
  }, [uploadedFiles]);

  // Derive this from messages
const lastAssistantMessage = [...messages].reverse().find((m) => m.from === "assistant");
const buttons = Array.isArray(lastAssistantMessage?.buttons) ? lastAssistantMessage.buttons : [];

const isConfirmationButtons =
  buttons.length === 2 &&
  buttons.every(
    (b) =>
      typeof b?.value === "string" &&
      ["yes", "no"].includes(b.value.toLowerCase())
  );

const awaitingSelection = buttons.length > 0 && !isConfirmationButtons;

  const getMatchedButtonValue = (input) => {
    const trimmed = input.trim().toLowerCase();
    const matchedButton = buttons.find(
      (b) =>
        (typeof b?.value === "string" && b.value.toLowerCase() === trimmed) ||
        (typeof b?.label === "string" && b.label.toLowerCase() === trimmed) ||
        (typeof b?.text === "string" && b.text.toLowerCase() === trimmed)
    );
    return matchedButton?.value ?? null;
  };

  // When awaitingSelection is active and the user types free text:
  // 1. Keep full history by appending the user's invalid message
  // 2. Re-append the assistant button message so the buttons are shown again
  // 3. Abort submission so the user is forced to use the buttons.
  const guardAwaitingSelection = (input) => {
    const matchedButtonValue = getMatchedButtonValue(input);

    // Skip guard for mid-flow (InProgress) intents — buttons are contextual, not selection gates
    const isIntentInProgress = lastAssistantMessage?.snapshotIntentState === 'InProgress';

    if (awaitingSelection && !matchedButtonValue && !isIntentInProgress) {
      const userTime = getCurrentTime();
      const assistantTime = getCurrentTime();
      setMessages((prev) => [
          ...prev,
          { from: "user", content: input.trim(), time: userTime },
          {
                ...lastAssistantMessage,
                  time: assistantTime
          },
      ]);
      setChatInput("");
      return { blocked: true, normalizedInput: null };
    }
    return {
      blocked: false,
      normalizedInput: matchedButtonValue ?? input,
    };
  };

  return (
      <>
        <Box
            sx={{
              position: "absolute",
              display: { xl: "none" },
              padding: 1,
              width: "100%",
            }}
        >
          <IconButton onClick={() => setAssistantSidebarOpen(true)}>
            <KeyboardDoubleArrowRight />
          </IconButton>
        </Box>
        {!switchedComponent ? (
            <Box
                component={"div"}
                sx={{
                  flex: { xs: "1", xl: "0.8" },
                  width: "100%",
                  height: "100%",
                  backgroundColor: "#f3f8fc",
                  overflowY: "auto",
                  overflowX: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  gap: 0.5,
                  borderRadius: "6px",
                  paddingX: { xs: "4rem", xl: "2rem" },
                }}
            >
              {!chatStarted ? (
                  <Box component={"div"} sx={{ textAlign: "center" }}>
                    <h4>How can I help you today? </h4>
                  </Box>
              ) : null}
              <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.2,

                    height: chatStarted ? "100%" : "auto",
                  }}
              >
                <Messages
                    scrollToLastItem={scrollToLastItem}
                    messages={messages}
                    setSwitchedComponent={setSwitchedComponent}
                    loading={loading}
                    handleModuleSubmit={handleModuleSubmit}
                    chatStarted={chatStarted}
                    setFilterData={setFilterData}
                    disabledFields={disabledFields}
                    setMessages={setMessages}
                />

                {/* Chat input box */}
                <Box
                    sx={{
                      position: "sticky",
                      bottom: 0,
                      width: "100%",
                      backgroundColor: "#f3f8fc",
                      display: "flex",
                      pb: 1,
                      flexDirection: "column",
                      // maxWidth: "60rem",
                      mx: "auto",
                    }}
                >
                  <form
                      onSubmit={handleChatSubmit}
                      style={{ display: "flex", alignItems: "start", gap: 2 }}
                  >
                    {!chatStarted ? (
                        <TextField
                            inputRef={autoFocusRef}
                            autoFocus
                            placeholder="Enter your query or select a task to get started"
                            minRows={1}
                            onChange={(e) => setChatInput(e.target.value)}
                            value={chatInput}
                            multiline
                            disabled={loading || disabledFields.chatInput}
                            InputProps={{
                              endAdornment: (
                                  <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        cursor: "pointer",
                                        gap: "0.5rem",
                                      }}
                                  >
                                    <Button onClick={() => setSwitchedComponent(true)} sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                      <img src="/images/apps/search_activity.svg" />
                                      <span style={{ color: "#005cb9", textWrap: "nowrap" , textTransform: "none" , letterSpacing: "0.5px" }}>Track Task</span>
                                    </Button>
                                    <AttachFile
                                        fontSize="small"
                                        sx={{
                                          color:
                                              disabledFields.fileDrop || loading
                                                  ? "#8B8B8B"
                                                  : "#005cb9",
                                          cursor:
                                              disabledFields.fileDrop || loading
                                                  ? "not-allowed"
                                                  : "pointer",
                                        }}
                                    />
                                    <hr
                                        style={{
                                          height: "1.5rem",
                                          borderLeft: "2px solid",
                                        }}
                                    />
                                    <InputAdornment
                                        sx={{
                                          height: "100%",
                                          cursor: disabledButtonCondition
                                              ? "not-allowed"
                                              : "pointer",
                                        }}
                                        position="end"
                                    >
                                      <SendOutlined
                                          fontSize="small"
                                          type="submit"
                                          onClick={handleChatSubmit}
                                          disabled={disabledButtonCondition}
                                          sx={{
                                            color: disabledButtonCondition
                                                ? "#d1d5db"
                                                : "#005cb9",
                                            pointerEvents: disabledButtonCondition
                                                ? "none"
                                                : "auto",
                                          }}
                                      />
                                    </InputAdornment>
                                  </Box>
                              ),
                            }}
                            maxRows={7}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                handleChatSubmit(e);
                              }
                            }}
                            variant="outlined"
                            sx={{
                              backgroundColor: "#fefefe",
                              width: "100%",
                              paddingRight: "1rem",
                              maxWidth: "60rem",
                              mx: "auto",
                              border: "1px solid #e5e7eb",
                              paddingLeft: "1rem",
                              borderRadius: "6px",
                              ":disabled": {
                                cursor: "not-allowed",
                              },
                              "& .MuiOutlinedInput-root": {
                                padding: 0,
                                "& fieldset": {
                                  border: "none",
                                },
                                "& .MuiInputBase-input": {
                                  padding: "14px 12px",
                                },
                              },
                            }}
                        />
                    ) : (
                        <Box
                            sx={{
                              width: "100%",
                              backgroundColor: "#fefefe",
                              maxWidth: "60rem",
                              mx: "auto",
                              border: "1px solid #e5e7eb",
                              paddingRight: "1rem",
                              paddingLeft: "1rem",
                              borderRadius: "6px",
                            }}
                        >
                          {disabledFields.fileDrop ? (
                              <TextField
                                  inputRef={autoFocusRef}
                                  autoFocus
                                  minRows={2.5}
                                  placeholder="Enter your query or select a task to get started"
                                  onChange={(e) => setChatInput(e.target.value)}
                                  value={chatInput}
                                  multiline
                                  disabled={loading || disabledFields.chatInput}
                                  maxRows={12}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                      e.preventDefault();
                                      const { blocked, normalizedInput } = guardAwaitingSelection(chatInput);
                                      if (blocked) return;
                                      handleChatSubmit(e, normalizedInput);
                                    }
                                  }}
                                  variant="outlined"
                                  sx={{
                                    width: "100%",
                                    ":disabled": {
                                      cursor: "not-allowed",
                                    },
                                    "& .MuiOutlinedInput-root": {
                                      padding: 0,
                                      "& fieldset": {
                                        border: "none",
                                      },
                                      "& .MuiInputBase-input": {
                                        paddingY: "14px",
                                      },
                                    },
                                  }}
                              />
                          ) : (
                              <Box
                                  ref={fileDropRef}
                                  sx={{
                                    minHeight: 76,
                                    height: "100%",
                                    maxHeight: "12rem",
                                    overflowY: "auto",
                                    display: "grid",
                                    gridTemplateColumns:
                                        "repeat(auto-fill, minmax(250px, 1fr))",
                                    gap: 1,
                                    paddingY: 1,
                                    outline: "none",
                                    "&:focus": {
                                      outline: "none",
                                    },
                                  }}
                                  onKeyDown={async (e) => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                      e.preventDefault();
                                      const { blocked, normalizedInput } = guardAwaitingSelection(chatInput);
                                      if (blocked) return;
                                      if (!disabledFields.fileDrop) {
                                        const validationResponse = await validateFileForIntent(intentName, uploadedFiles, slotName);
                                        if (!validationResponse) return;
                                        clickedSubmit.current = true;
                                        setFileNamesSet(new Set());
                                      }
                                      await handleChatSubmit(e, normalizedInput);
                                    }
                                  }}
                                  tabIndex={0}
                              >
                                {uploadedFiles.length > 0 &&
                                    uploadedFiles.map((file, index) => (
                                        <Box
                                            key={file.name}
                                            sx={{
                                              border: loading ? "none" : "1.5px solid #005cb9",
                                              paddingX: 1,
                                              paddingY: 0.7,
                                              borderRadius: "5px",
                                              width: "100%",
                                              display: "flex",
                                              alignItems: "center",
                                              height: "2.5rem",
                                              gap: 0.5,
                                              backgroundColor: loading ? "#e5e7eb" : "#ffffff",
                                            }}
                                        >
                                          <Typography
                                              style={{
                                                fontSize: "0.8rem",
                                                width: "100%",
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                              }}
                                              title={file.name}
                                          >
                                            {file.name}
                                          </Typography>
                                          <Close
                                              sx={{
                                                color: "black",
                                                fontSize: "0.8rem",
                                                cursor: "pointer",
                                              }}
                                              onClick={() => handleDelete(index)}
                                              disabled={loading}
                                          />
                                        </Box>
                                    ))}
                              </Box>
                          )}

                          <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                borderTop: "2px solid #e5e7eb",
                              }}
                          >
                            <div style={{ display: "flex", gap: "0.75rem" , width: "100%"  , height: "3rem"}}>
                              <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.3rem",
                                    cursor: "pointer",
                                  }}
                                  onClick={() => {
                                    setSwitchedComponent(true);
                                    setFilterData("");
                                  }}
                              >
                                <img src="/images/apps/search_activity.svg" />
                                <span style={{ color: "#005cb9" }}>Track Task</span>
                              </div>
                              <FileDrop
                                  fileNamesSet={fileNamesSet}
                                  setUploadedFiles={setUploadedFiles}
                                  setFileNamesSet={setFileNamesSet}
                                  disabledFields={disabledFields}
                                  loading={loading}
                                  uploadedFiles={uploadedFiles}
                                  intentName={intentName}
                              />
                            </div>
                            <Box
                                sx={{
                                  display: "flex",
                                  gap: "0.5rem",
                                  alignItems: "center",
                                }}
                            >
                            {((awaitingSelection || (intentName.length > 0 && dialogActionType?.toLowerCase() !== "close")) && !loading) ? (
                                  <IconButton
                                      onClick={handleCancelOpen}
                                      sx={{
                                        color: "#FC5C5C",
                                        ":disabled": {
                                          cursor: "not-allowed",
                                          color: "#8b8b8b",
                                          display: "none"
                                        },
                                      }}
                                      size="small"
                                  >
                                    <StopCircleOutlined />
                                  </IconButton>
                              ) : null}

                              <SendOutlined
                                  fontSize="small"
                                  type="submit"
                                  onClick={async (e) => {
                                    const { blocked, normalizedInput } = guardAwaitingSelection(chatInput);
                                    if (blocked) return;
                                    if (!disabledFields.fileDrop) {
                                      const validationResponse =
                                          await validateFileForIntent(
                                              intentName,
                                              uploadedFiles,
                                              slotName
                                          );
                                      if (!validationResponse) return;
                                      clickedSubmit.current = true;
                                      setFileNamesSet(new Set());
                                    }
                                    await handleChatSubmit(e, normalizedInput);
                                  }}
                                  disabled={
                                    !disabledFields.fileDrop
                                        ? uploadedFiles.length === 0
                                        : disabledButtonCondition
                                  }
                                  sx={{
                                    color: !disabledFields.fileDrop
                                        ? uploadedFiles.length === 0
                                            ? "#8b8b8b"
                                            : "#005cb9"
                                        : disabledButtonCondition
                                            ? "#8b8b8b"
                                            : "#005cb9",
                                    pointerEvents: (
                                        !disabledFields.fileDrop
                                            ? uploadedFiles.length === 0
                                            : disabledButtonCondition
                                    )
                                        ? "none"
                                        : "auto",
                                  }}
                              />
                            </Box>
                          </Box>
                        </Box>
                    )}
                  </form>
                </Box>
              </Box>
              <Modal
                  open={!!cancelButton}
                  onClose={handleCancelClose}
                  closeAfterTransition
                  sx={{
                    height: "100%",
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
              >
                <Box
                    sx={{
                      backgroundColor: "white",
                      borderRadius: "8px",
                      padding: "2rem",
                    }}
                >
                  <Typography variant="h5">
                    Are you sure you want to cancel the request?
                  </Typography>
                  <Box
                      sx={{
                        mt: 3,
                        display: "flex",
                        justifyContent: "flex-end",
                      }}
                  >
                    <Button
                        variant="outlined"
                        onClick={handleCancelClose}
                        sx={{
                          mr: 2,
                          borderRadius: "7px",
                          borderColor: "#005cb9",
                          color: "#005cb9",
                        }}
                    >
                      No
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                          handleStop();
                          setFileNamesSet(new Set());
                          handleCancelClose();
                          const time = getCurrentTime();
                          setMessages((prev) => [
                              ...prev,
                              { from: "assistant", content: "Request cancelled.", time },
                          ]);
                        }}
                        sx={{
                          borderRadius: "7px",
                          backgroundColor: "#005cb9",
                          color: "white",
                          "&:hover": { backgroundColor: "#005cb9" },
                        }}
                    >
                      Yes
                    </Button>
                  </Box>
                </Box>
              </Modal>
            </Box>
        ) : (
            <IpAssistantTask
                filterData={filterData}
                handleStop={handleStop}
                chatSessionId={chatSessionId}
                setMessages={setMessages}
                setChatStarted={setChatStarted}
                setSwitchedComponent={setSwitchedComponent}
                setLoading={setLoading}
            />
        )}
      </>
  );
};

Chats.propTypes = {
  switchedComponent: PropTypes.bool.isRequired,
  uploadedFiles: PropTypes.array.isRequired,
  chatStarted: PropTypes.bool.isRequired,
  messages: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  handleModuleSubmit: PropTypes.func.isRequired,
  disabledFields: PropTypes.object.isRequired,
  scrollToLastItem: PropTypes.object.isRequired,
  setSwitchedComponent: PropTypes.func.isRequired,
  chatInput: PropTypes.string.isRequired,
  setChatInput: PropTypes.func.isRequired,
  charactersLimit: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  handleChatSubmit: PropTypes.func.isRequired,
  setAssistantSidebarOpen: PropTypes.func.isRequired,
  intentList: PropTypes.oneOfType([PropTypes.array, PropTypes.object]).isRequired,
  autoFocusRef: PropTypes.object.isRequired,
  chatSessionId: PropTypes.string.isRequired,
  setMessages: PropTypes.func.isRequired,
  setChatStarted: PropTypes.func.isRequired,
  setLoading: PropTypes.func.isRequired,
  handleStop: PropTypes.func.isRequired,
  setUploadedFiles: PropTypes.func.isRequired,
  intentName: PropTypes.string.isRequired,
  clickedSubmit: PropTypes.shape({
    current: PropTypes.bool,
  }).isRequired,
  setFilterData: PropTypes.func.isRequired,
  filterData: PropTypes.string.isRequired,
  dialogActionType: PropTypes.string.isRequired,
  slotName: PropTypes.string.isRequired,
};

export default Chats;
